/**
 * The small cloud button ("☁ Kaydet" / "☁ Hesabım") on a game's top bar that opens the phone login dialog. Hidden
 * until the page's account answers: with no identity on the API (flag off, offline, bot runs) it never appears and
 * the screen is exactly as before. Opening the scene also starts the account (and so the save sync).
 */
import Phaser from "phaser";
import { pageAccount, type AccountState } from "./account";
import { openAccountDialog } from "./accountDialog";
import { pick, type Lang } from "./lang";
import { FONT } from "./ui";

const LABEL = {
  guest: { tr: "Kaydet", en: "Save" },
  member: { tr: "Hesabım", en: "Account" },
} as const;

const W = 176;
const H = 58;

const css = (c: number): string => `#${c.toString(16).padStart(6, "0")}`;

function drawCloud(g: Phaser.GameObjects.Graphics, color: number, check: boolean): void {
  g.clear();
  g.fillStyle(color, 1);
  g.fillCircle(-9, 3, 9).fillCircle(1, -3, 12).fillCircle(11, 4, 8).fillRoundedRect(-18, 2, 37, 11, 5);
  if (check) {
    g.lineStyle(3.5, 0x1b1f3a, 1);
    g.beginPath();
    g.moveTo(-5, 3);
    g.lineTo(0, 8);
    g.lineTo(8, -2);
    g.strokePath();
  }
}

/** Add the button at (x, y) in design space; it shows itself when the account is usable. */
export function accountButton(scene: Phaser.Scene, x: number, y: number, opts: { lang: Lang; accent: number; depth?: number }): Phaser.GameObjects.Container {
  const bg = scene.add.graphics();
  const icon = scene.add.graphics().setPosition(-W / 2 + 34, 0);
  const label = scene.add.text(-W / 2 + 62, 0, "", { fontFamily: FONT, fontSize: "22px", color: "#ffffff", fontStyle: "bold" }).setOrigin(0, 0.5);
  const button = scene.add.container(x, y, [bg, icon, label]).setSize(W, H).setDepth(opts.depth ?? 51).setVisible(false);
  button.setInteractive({ useHandCursor: true }).on("pointerdown", (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
    ev.stopPropagation();
    scene.tweens.add({ targets: button, scale: 0.94, duration: 60, yoyo: true, onComplete: () => openAccountDialog({ lang: opts.lang, accent: css(opts.accent) }) });
  });
  const account = pageAccount();
  if (!account) return button;
  let state: AccountState = account.current;
  let held = false;
  const apply = () => {
    if (!button.scene) return;
    const member = state.status === "member";
    const shown = !held && (member || (state.status === "guest" && state.phoneLogin));
    button.setVisible(shown);
    if (button.input) button.input.enabled = shown;
    bg.clear();
    bg.fillStyle(0x262b4d, 1).fillRoundedRect(-W / 2, -H / 2, W, H, H / 2);
    bg.lineStyle(3, opts.accent, member ? 0.55 : 1).strokeRoundedRect(-W / 2, -H / 2, W, H, H / 2);
    drawCloud(icon, opts.accent, member);
    label.setText(pick(opts.lang, member ? LABEL.member : LABEL.guest));
  };
  button.setData("hold", (on: boolean) => {
    held = on;
    apply();
  });
  const off = account.subscribe((next) => {
    state = next;
    apply();
  });
  scene.events.once("shutdown", off);
  scene.events.once("destroy", off);
  void account.start();
  return button;
}

/** Hide the button while it would get in the way (a Harf Yılanı run is on), show it again after. */
export function holdAccountButton(button: Phaser.GameObjects.Container, hold: boolean): void {
  (button.getData("hold") as ((on: boolean) => void) | undefined)?.(hold);
}
